import BreadCrumb from "components/Common/BreadCrumb";
import TableContainer from "components/Common/TableContainer";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CountUp from "react-countup";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardBody, CardHeader, Col, Container, Row, Spinner } from "reactstrap";
import { createSelector } from "reselect";
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import { getTokens as onGetTokens } from "../../store/thunks";
import { formatNumberWithCommas, formatScientificNumber, formatTokenNumber } from "helpers/format";
import CopyWrapper from "components/Common/CopyWrapper";
import { ToastContainer } from "react-toastify";
import { Option } from "api/types/_public";
import LabelWrapper from "components/Common/LabelWrapper";
import DropdownOption from "components/Common/DropdownOption";
import { LINK_FOR_NO_IMAGE, TOKEN_PROGRAMS } from "helpers/constans";
import DropdownStatus from "components/Common/DropdownStatus";
import TooltipCustom from "components/Common/TooltipCustom";
import { Link } from "react-router-dom";
import CountdownCircle from "./CountdownCircle";
import TokenForm from "./TokenForm";

const TYPE_SELECT_DEFAULT: string = "id";

export default function Token() {
  const { t, i18n } = useTranslation();

  const TYPE_TOKEN_OPTIONS_LANG: any = [{ value: 'Internal', label: 'Internal' }, { value: 'External', label: 'External' }]?.map((item: any) => ({ value: item?.value, label: t(item?.label) }));

  const TOKEN_PROGRAMS_LANG = TOKEN_PROGRAMS.map((item) => ({
    label: t(item.label),
    value: item.value
  }));

  const [query, setQuery]: any = useQueryParams({
    time_request: withDefault(NumberParam, 0),
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 100),
    sort_by: withDefault(StringParam, TYPE_SELECT_DEFAULT),
    name: withDefault(StringParam, ""),
    type: withDefault(StringParam, ""),
  });

  const [typeSearch, setTypeSearch] = useState<Option | null>(TYPE_TOKEN_OPTIONS_LANG?.find((item: any) => String(item?.value) === String(query?.type)) || null);

  const [isCallingInterval, setIsCallingInterval] = useState<boolean>(false);

  const [isCountdown, setIsCountdown] = useState<boolean>(false);

  const [isShowModalAddToken, setIsShowModalAddToken] = useState<boolean>(false);

  const dispatch: any = useDispatch();

  const selectLayoutState = (state: any) => {
    return state.Token;
  };

  const TokenProperties = createSelector(selectLayoutState, (state) => ({
    tokens: state.tokens,
    isTokenSuccess: state.isTokenSuccess,
    isTokenLoading: state.isTokenLoading,
    error: state.error,
  }));

  // Inside your component
  const { tokens, error, isTokenLoading } = useSelector(TokenProperties);

  useEffect(() => {
    dispatch(onGetTokens(query));

    const apiIntervalId = setInterval(() => {
      setIsCallingInterval(true);
      setIsCountdown((_prev) => !_prev);
      dispatch(onGetTokens(query));
    }, 20000);

    return () => {
      clearInterval(apiIntervalId);
      setIsCallingInterval(false);
    };
  }, [dispatch, JSON.stringify(query)]);

  const searchData = () => {
    setIsCallingInterval((_prev) => false);
    setQuery({
      ...query,
      type: typeSearch?.value,
      page: 1,
      time_request: + new Date()
    });
  };

  const resetData = () => {
    setIsCallingInterval((_prev) => false);
    setQuery({
      type: '',
      sort_by: TYPE_SELECT_DEFAULT,
      time_request: + new Date(),
      page: 1,
    }, "push")
    setTypeSearch(null);
  };

  const triggerRefresh = () => {
    resetData();
    setIsShowModalAddToken(false);
  };

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("ID"),
        accessor: "ID",
        filterable: true,
        sortable: false,
        thWidth: 50,
        Cell: (cell: any) => (
          <>
            <span style={{ minWidth: "50px" }}>{cell?.value}</span>
          </>
        ),
      },
      {
        Header: t("Logo"),
        accessor: "logo",
        filterable: true,
        sortable: false,
        thWidth: 230,
        thClass: "text-start",
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          return (
            <>
              <Link to={`https://solscan.io/token/${item?.mint}`} target="_blank" className="d-inline-block">
                <div className="d-flex text-start justify-content-start align-items-center gap-2 m-auto" style={{ minWidth: '200px' }}>
                  <div className="w-[44px]">
                    <img
                      src={
                        item?.logo || LINK_FOR_NO_IMAGE
                      }
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "22px",
                      }}
                      className="flex-shrink-0 object-fit-cover m-auto w-[44px] h-[44px] !rounded-full"
                      alt={item?.keyword}
                    />
                  </div>
                  <div className="d-flex flex-column">
                    <div className="text-start" style={{ minWidth: "120px" }}>{item?.pair?.pairLabel ? <span><strong className="bold me-1 fs-15">{String(item?.pair?.pairLabel)?.split('/')[0]}</strong>/<span className="ms-1 fs-12">{String(item?.pair?.pairLabel)?.split('/')[1]}</span></span> : (item?.symbol || '-')}</div>
                    <div className="text-start fs-14 text-muted" style={{ minWidth: "100px" }}>
                      {/* {item?.name || "-"} */}
                      {!!item?.mint ? <TooltipCustom
                        title={t('Address')}
                        className="d-inline-block vertical-align-middle"
                        style={{ transform: 'translateY(2px)' }}
                      >
                        <CopyWrapper isShow={true} contentCopy={item?.mint}>{(item?.mint || '')?.slice(0, 5) + "..." + (item?.mint || '')?.slice(-4)}</CopyWrapper>
                      </TooltipCustom> : (item?.name || "-")}
                    </div>
                  </div>
                </div>
              </Link>
            </>
          );
        },
      },
      // {
      //   Header: t("Name"),
      //   accessor: "name",
      //   filterable: true,
      //   sortable: false,
      //   thWidth: 160,
      //   Cell: (cell: any) => {
      //     const item = cell?.row?.original;
      //     return (
      //       <>
      //         <div style={{ minWidth: "100px" }}>{item?.name || "-"}</div>
      //       </>
      //     );
      //   },
      // },
      // {
      //   Header: t("Symbol"),
      //   accessor: "symbol",
      //   filterable: true,
      //   sortable: false,
      //   thWidth: 100,
      //   Cell: (cell: any) => {
      //     const item = cell?.row?.original;
      //     return (
      //       <>
      //         <div style={{ minWidth: "80px" }}>{item?.symbol || "-"}</div>
      //       </>
      //     );
      //   },
      // },
      {
        Header: t('Filter Type'),
        accessor: "type",
        filterable: false,
        sortable: false,
        thClass: 'text-center',
        thWidth: 80,
        Cell: (cell: any) => {
          const COLOR: any = {
            Internal: 'success',
            External: 'primary',
          };
          return (
            <div className="text-center" style={{ minWidth: '60px' }}>
              <span className={`rounded-pill badge bg-${COLOR?.[cell?.value] || 'secondary'}`}>{TYPE_TOKEN_OPTIONS_LANG?.find((_i: any) => String(_i?.value) === String(cell?.value))?.label}</span>
            </div>
          );
        }
      },
      {
        Header: t("Fully Diluted Value"),
        accessor: "fullyDilutedValue",
        filterable: true,
        sortable: false,
        thWidth: 230,
        thClass: "text-end",
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          return (
            <>
              <div style={{ minWidth: "200px" }} className="text-end fs-15 bold">
                <TooltipCustom
                  title={item?.fullyDilutedValue}
                  className="d-inline-block vertical-align-middle ms-1"
                  style={{ transform: 'translateY(2px)' }}
                >
                  {formatTokenNumber(item?.fullyDilutedValue, 2, '$')}
                </TooltipCustom>
              </div>
            </>
          );
        },
      },
      {
        Header: t("Total Supply"),
        accessor: "totalSupply",
        filterable: true,
        sortable: false,
        thWidth: 230,
        thClass: "text-end",
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          return (
            <>
              <div style={{ minWidth: "200px" }} className="text-end fs-15 bold">
                <TooltipCustom
                  title={item?.totalSupply}
                  className="d-inline-block vertical-align-middle ms-1"
                  style={{ transform: 'translateY(2px)' }}
                >
                  {formatTokenNumber(item?.totalSupply, 0, '')}
                </TooltipCustom>
              </div>
            </>
          );
        },
      },
      // {
      //   Header: t('Liquidity'),
      //   accessor: "decimals",
      //   filterable: true,
      //   sortable: false,
      //   thWidth: 230,
      //   thClass: 'text-end',
      //   Cell: (cell: any) => {
      //     const item = cell?.row?.original;
      //     return (<>
      //       <div style={{ minWidth: '200px' }} className="text-end">
      //         <div className="fs-15 bold">
      //           <TooltipCustom
      //             title={formatNumberWithCommas(item?.pair?.totalLiquidityUsd)}
      //             className="d-inline-block vertical-align-middle ms-1"
      //             style={{ transform: 'translateY(2px)' }}
      //           >
      //             {formatTokenNumber(item?.pair?.totalLiquidityUsd, 1, '$')}
      //           </TooltipCustom>
      //         </div>
      //         <div className="text-muted fs-12">24H Vol: <span className={Number(item?.pair?.liquidityPercentChange?.['24h']) < 0 ? 'text-danger' : 'text-success'}>
      //           <TooltipCustom
      //             title={formatNumberWithCommas(item?.pair?.liquidityPercentChange?.['24h'])}
      //             className="d-inline-block vertical-align-middle ms-1"
      //             style={{ transform: 'translateY(2px)' }}
      //           >
      //             {formatTokenNumber(item?.pair?.liquidityPercentChange?.['24h'], 2, '$')}
      //           </TooltipCustom>
      //         </span></div>
      //       </div>
      //     </>)
      //   },
      // },
      {
        Header: t('Current Usd Price'),
        accessor: "currentUsdPrice",
        filterable: true,
        sortable: false,
        thWidth: 230,
        thClass: 'text-end',
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          return (<>
            <div style={{ minWidth: '200px' }} className="text-end">
              <div className="fs-15 bold">
                <TooltipCustom
                  title={item?.price?.usdPrice}
                  className="d-inline-block vertical-align-middle ms-1"
                  style={{ transform: 'translateY(2px)' }}
                >
                  <div className="d-flex align-items-center gap-1"><span style={{ fontWeight: '500' }}>{formatScientificNumber(item?.price?.usdPrice, 9, '$')}</span>
                    <span style={{ marginBottom: '2px' }}><CountdownCircle change={isCountdown} /></span>
                  </div>
                </TooltipCustom>
              </div>
              <div className="text-muted fs-12">
                <TooltipCustom
                  title={item?.price?.usdPrice24hrPercentChange}
                  className="d-inline-block vertical-align-middle ms-1"
                  style={{ transform: 'translateY(2px)' }}
                >
                  <span className="text-muted me-1">{t('Price')} 24h:</span>
                  <span className={Number(item?.price?.usdPrice24hrPercentChange) < 0 ? 'text-danger' : 'text-success'}>
                    {formatScientificNumber(item?.price?.usdPrice24hrPercentChange, 9, '')}%
                  </span>
                </TooltipCustom>
              </div>
            </div>
          </>)
        },
      },
      {
        Header: t("Token"),
        accessor: "mint",
        filterable: true,
        sortable: false,
        thWidth: 520,
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          return (
            <>
              <div className="d-flex flex-column gap-1" style={{ minWidth: "500px" }}>
                <Link to={`https://solscan.io/token/${item?.mint}`} target="_blank" className="d-inline-block">
                  <div className="d-flex">
                    <CopyWrapper isShow={true} contentCopy={item?.mint}> {item?.mint || "-"} </CopyWrapper>
                  </div>
                </Link>
                {/* <div className="d-flex">
                  <span className="text-muted me-1">{t('Pair')}: </span>  <CopyWrapper isShow={true} contentCopy={item?.quote}> {item?.quote || "-"} </CopyWrapper>
                </div>
                <div className="d-flex">
                  <span className="text-muted me-1">{t('Pair Address')}: </span>  <CopyWrapper isShow={true} contentCopy={item?.pair?.pairAddress}> {item?.pair?.pairAddress || "-"} </CopyWrapper>
                </div> */}
              </div>
            </>
          );
        },
      },
      {
        Header: t("Program"),
        accessor: "tokenProgram",
        filterable: true,
        sortable: false,
        thWidth: 230,
        thClass: "text-end",
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          return (
            <>
              <div style={{ minWidth: "200px" }} className="text-end fs-15 bold">
                {TOKEN_PROGRAMS_LANG?.find((_i: any) => String(_i?.value) === String(item?.tokenProgram))?.label || '-'}
              </div>
            </>
          );
        },
      },
      {
        Header: t("Created at"),
        accessor: "CreatedAt",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end",
        Cell: (cell: any) => {
          const arrDate = String(cell?.value || "").split("T");
          return (
            <div className="text-end" style={{ minWidth: "120px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{String(arrDate?.[1] || "")?.split('.')[0]}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Updated at"),
        accessor: "UpdatedAt",
        filterable: true,
        sortable: false,
        thWidth: 140,
        thClass: "text-end",
        Cell: (cell: any) => {
          const arrDate = String(cell?.value || "").split("T");
          return (
            <div className="text-end pe-2" style={{ minWidth: "120px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{String(arrDate?.[1] || "")?.split('.')[0]}</span>{" "}
              </span>
            </div>
          );
        },
      },
    ],
    [i18n?.language, query, isCountdown]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 });
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return { ..._prev, ...sortBy };
    });
  }, []);

  useEffect(() => {
    document.title = `${t("Home")} - ${t("Token")}`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("Token")} pageTitle={t("Home")} />
          <Row>
            <Col lg={12}>
              <Card
                id="customerList"
                style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
              >
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <div className="col-5 con-sm-6">
                      <div>
                        <h5 className="card-title mb-0">
                          <span className="me-2">{t("Total")}:</span>
                          {(isTokenLoading && !isCallingInterval) ? <Spinner size="sm" className="text-primary"></Spinner> : (<CountUp
                            start={0}
                            end={tokens?.pagination?.total || 0}
                            duration={1}
                            className="text-primary"
                          />)}
                        </h5>
                      </div>
                    </div>
                    <div className="col-7 con-sm-6">
                      <div className="d-flex justify-content-end">
                        <button className="btn btn-primary" onClick={() => {
                          setIsShowModalAddToken(true);
                        }}>
                          <i className="ri-add-line align-bottom me-1"></i>{" "}
                          {t('Add Token')}
                        </button>
                      </div>
                    </div>
                  </Row>
                </CardHeader>
                <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                  <Row className="g-4 align-items-center mt-0">
                    <Col sm={6} md={4} xl={3} xxl={2} className='mt-3'>
                      <LabelWrapper label={t('Filter Type')} isShow={true}>
                        <DropdownStatus
                          name="type"
                          dataList={TYPE_TOKEN_OPTIONS_LANG || []}
                          placeholder={`${t("Filter Type")}...`}
                          className="dropdown-status-rounded z-hight"
                          classNamePrefix="name-prefix"
                          initialValue={typeSearch || null}
                          onChangeSelect={(e: any) => setTypeSearch(e)}
                          isHasOptionAll={true}
                          optionAll={{ label: t('All'), value: '' }}
                          colors={{
                            Internal: 'success',
                            External: 'primary',
                          }}
                          isColorObject={true}
                        />
                      </LabelWrapper>
                    </Col>
                    <Col sm={6} md={8} xl={9} xxl={10} className="hstack gap-1 justify-content-center justify-content-sm-start mt-3">
                      <div>
                        <button
                          type="button"
                          className="btn btn-primary me-2  bg-[#009ef7] hover:bg-[#0086d2]"
                          onClick={searchData}
                          disabled={(isTokenLoading && !isCallingInterval)}
                        >
                          <i className="ri-search-line align-bottom me-1"></i>{" "}
                          {t('Button Search')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary fs-14  bg-[#878a99] hover:bg-[#737582]"
                          onClick={resetData}
                        >
                          <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                          {t('Button Reset')}
                        </button>
                      </div>
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-3">
                  <TableContainer
                    className="custom-header-css"
                    divClass="table-responsive table-card"
                    tableClass="align-middle sticky-table-token"
                    theadClass="table-light"
                    columns={columns}
                    data={tokens?.list?.length ? tokens?.list : []}
                    customPageSize={query.limit}
                    customPageIndex={query.page - 1}
                    totalRecords={tokens?.pagination?.total}
                    // customPageCount={1}
                    customPageCount={Math.ceil(
                      Number(tokens?.pagination?.total) /
                      Number(tokens?.pagination?.limit)
                    )}
                    handleChangePage={handleChangePage}
                    manualSorting={true}
                    sorting={{
                      sort_by: query.sort_by,
                      order_by: query.order_by,
                    }}
                    handleChangeSorting={handleChangeSorting}
                    isLoading={(isTokenLoading && !isCallingInterval)}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        <TokenForm isOpen={isShowModalAddToken} onClose={() => setIsShowModalAddToken(false)} triggerRefresh={triggerRefresh} />
        <ToastContainer autoClose={2000} limit={1} />
      </div>
    </React.Fragment>
  );
}
